import React from 'react';
import { Link } from 'react-router-dom';
import Reveal from '../components/Reveal';
import SpotlightCard from '../components/SpotlightCard';
import { ArrowRight } from '../components/Icons';
import usePageTitle from '../hooks/usePageTitle';
import { TEAM, VALUES } from '../data/content';
import './AboutPage.css';

const initials = (name) =>
  name
    .split(' ')
    .map((part) => part[0])
    .join('');

const AboutPage = () => {
  usePageTitle('About');

  return (
    <div className="about-page">
      <section className="page-hero">
        <div className="container">
          <p className="eyebrow">About us</p>
          <h1>
            Our differences are our <span className="highlight">strengths.</span>
          </h1>
          <p className="lead">Get to know our team and our mission in the tech industry.</p>
        </div>
      </section>

      {/* Story */}
      <section className="section">
        <div className="container story-grid">
          <Reveal className="story-copy">
            <p className="eyebrow">Our story</p>
            <h2 className="section-title">Built on a simple, powerful idea.</h2>
            <p>
              Autistic Dev was founded in 2020 with a simple but powerful mission: to harness the unique strengths of
              neurodiversity in creating exceptional technology solutions.
            </p>
            <p>
              Our team consists of talented developers, designers and consultants who bring their diverse perspectives
              and specialized skills to every project we undertake.
            </p>
            <p>
              The attention to detail, pattern recognition and innovative problem-solving that often accompany
              neurodivergent thinking are invaluable assets in technology development.
            </p>
          </Reveal>
          <Reveal delay={150} as="figure" className="story-quote">
            <svg className="quote-mark" width="48" height="36" viewBox="0 0 48 36" aria-hidden="true">
              <path
                d="M0 36V20C0 8.4 6 1.7 18 0l2 6c-6 1.6-9 5.3-9 11h8v19H0zm28 0V20c0-11.6 6-18.3 18-20l2 6c-6 1.6-9 5.3-9 11h8v19H28z"
                fill="currentColor"
              />
            </svg>
            <blockquote>
              We don't build for the average user. There isn't one. We build for the full, beautiful range of how
              people think.
            </blockquote>
            <figcaption>The Autistic Dev team</figcaption>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="section section-alt">
        <div className="container">
          <Reveal className="section-head">
            <p className="eyebrow">Our values</p>
            <h2 className="section-title">What we stand for.</h2>
          </Reveal>
          <div className="values-grid">
            {VALUES.map((value, i) => (
              <Reveal key={value.title} delay={i * 90}>
                <SpotlightCard className="value-card">
                  <span className="value-index">/{String(i + 1).padStart(2, '0')}</span>
                  <h3>{value.title}</h3>
                  <p>{value.text}</p>
                </SpotlightCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="section">
        <div className="container">
          <Reveal className="section-head">
            <p className="eyebrow">Our team</p>
            <h2 className="section-title">The minds behind the work.</h2>
          </Reveal>
          <div className="team-grid">
            {TEAM.map((member, i) => (
              <Reveal key={member.id} delay={i * 110}>
                <SpotlightCard className="team-card">
                  <div className="team-avatar" style={{ '--h': 40 + i * 110 }} aria-hidden="true">
                    {initials(member.name)}
                  </div>
                  <h3>{member.name}</h3>
                  <p className="team-role">{member.role}</p>
                  <p>{member.bio}</p>
                  <p className="team-power">
                    <span>Superpower</span>
                    {member.superpower}
                  </p>
                </SpotlightCard>
              </Reveal>
            ))}
          </div>
          <Reveal className="join-band">
            <div>
              <h3>Think differently? So do we.</h3>
              <p>We're always happy to hear from neurodivergent developers and designers.</p>
            </div>
            <Link to="/contact" className="btn btn-ghost">
              Get in touch <ArrowRight />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
